import { PayPalButtons, usePayPalScriptReducer } from "@paypal/react-paypal-js";

import { useLanguage } from "../../contexts/LanguageContext";
import { useToast } from "../../contexts/ToastContext";
import { useUser } from "../../contexts/UserContext";
import apiService from "../../services/apiService";

import "./PayPalButton.css";

const PayPalButton = ({ plan, onSuccess, onError }) => {
  const { showError } = useToast();
  const [{ options, isPending, isRejected }, dispatch] =
    usePayPalScriptReducer();
  const { t } = useLanguage();
  const { user } = useUser();

  // Check if PayPal is properly configured
  const paypalClientId = process.env.REACT_APP_PAYPAL_CLIENT_ID;
  const isValidPayPalClientId =
    paypalClientId &&
    paypalClientId.length > 10 &&
    paypalClientId !== "test" &&
    paypalClientId !== "sb" &&
    paypalClientId !== "PAYPAL_NOT_CONFIGURED";

  // Check if script failed to load or if client ID is invalid
  if (isRejected || !isValidPayPalClientId) {
    return (
      <div className="paypal-button-container">
        <div className="paypal-error">
          <p>
            {t("paypalButton.notConfigured") ||
              "PayPal is not configured. Please contact support."}
          </p>
          {isRejected && (
            <p style={{ fontSize: "12px", color: "#666", marginTop: "5px" }}>
              Error loading PayPal SDK
            </p>
          )}
        </div>
      </div>
    );
  }

  // Additional check for options
  if (!options || !options["client-id"]) {
    return (
      <div className="paypal-button-container">
        <div className="paypal-loading">
          <div className="spinner"></div>
          <p>{t("paypalButton.loading") || "Loading PayPal..."}</p>
        </div>
      </div>
    );
  }

  const createOrder = async () => {
    try {
      // Ask server to create order and return id
      const res = await apiService.createPayPalOrder({ plan });
      if (!res?.orderID) throw new Error("Missing orderID from server");
      return res.orderID;
    } catch (error) {
      onError && onError(error);
      showError(t("paypalButton.onError"));
      throw error;
    }
  };

  const onApprove = async (data) => {
    try {
      // Ask server to capture and upgrade
      const result = await apiService.capturePayPalOrder({
        orderID: data.orderID,
        plan,
        userId: user?.id,
      });

      if (result?.success) {
        onSuccess && onSuccess(result);
      } else {
        throw new Error(t("paypalButton.error"));
      }
    } catch (error) {
      onError && onError(error);
      showError(t("paypalButton.onError"));
    }
  };

  const onErrorHandler = (err) => {
    console.error("PayPal button error:", err);
    onError && onError(err);
    showError(err?.message || t("paypalButton.onError"));
  };

  const onCancel = () => {
    showError(t("paypalButton.cancel"));
  };

  return (
    <div className="paypal-button-container">
      {isPending ? (
        <div className="paypal-loading">
          <div className="spinner"></div>
          <p>{t("paypalButton.loading")}</p>
        </div>
      ) : (
        <PayPalButtons
          style={{
            layout: "horizontal",
            color: "blue",
            shape: "rect",
            label: "pay",
            height: 40,
          }}
          createOrder={createOrder}
          onApprove={onApprove}
          onError={onErrorHandler}
          onCancel={onCancel}
        />
      )}
    </div>
  );
};

export default PayPalButton;
